// Today subview of the adult Chores chart.
//
// Layout (desktop):  [me column] [partner columns…] [side rail]
// Layout (mobile):   stacked, side rail full-width below.
//
// "Today's bucket" for a person = chores where thisWeek === person.id
// AND (nextDue === today OR nextDue < today AND lastDone !== today).
// Overdue items render with the amber accent — never red. The side
// rail shows "Behind" count, the next 5 upcoming, and what's already
// been done today.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { AdultChore, HouseholdMember } from "../../lib/rpc";

interface TodayChoresProps {
  chores: AdultChore[];
  members: HouseholdMember[];
  onDone: (chore: AdultChore) => void;
  onEdit: (chore: AdultChore) => void;
  onDelete: (chore: AdultChore) => void;
}

export function TodayChores({ chores, members, onDone, onEdit, onDelete }: TodayChoresProps) {
  // Use the BROWSER's local timezone for "today," not UTC. The server
  // computes next_due in the user's TZ via Intl.DateTimeFormat, so
  // using UTC here makes a chore created Chicago-today look "overdue"
  // when the user opens the chart Chicago-evening (= UTC next day).
  // Mirroring the en-CA YYYY-MM-DD format keeps the lexicographic
  // comparison against the server's date strings exact.
  const todayIso = useMemo(
    () => new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date()),
    [],
  );

  // Adults only — kids show up in their own KidApp Chores tab; this
  // chart is the partner-share surface and including kids would muddy
  // the columns. Sort alphabetically by display id for stability.
  const adults = useMemo(
    () => members.filter((m) => m.role !== "kid").sort((a, b) => a.userId.localeCompare(b.userId)),
    [members],
  );

  const byPerson = useMemo(() => {
    const map = new Map<string, AdultChore[]>();
    const push = (userId: string, c: AdultChore) => {
      const arr = map.get(userId) || [];
      arr.push(c);
      map.set(userId, arr);
    };
    for (const c of chores) {
      const dueOrOverdue = (c.nextDue <= todayIso) && c.lastDone !== todayIso;
      if (!dueOrOverdue) continue;
      if (c.assignmentMode === "parallel" && c.assignees.length > 0) {
        // Parallel chores render in EVERY assignee's column EXCEPT
        // for those who've already done it today. Per-user completion
        // is tracked on the server via adult_chore_completions; the
        // list ships completedTodayBy so we can hide the row from
        // the person who completed it without affecting their partner.
        const doneSet = new Set(c.completedTodayBy || []);
        for (const a of c.assignees) {
          if (doneSet.has(a)) continue;
          push(a, c);
        }
        continue;
      }
      const owner = c.thisWeek;
      if (!owner) continue;
      push(owner, c);
    }
    return map;
  }, [chores, todayIso]);

  const overdue = useMemo(
    () => chores.filter((c) => c.nextDue < todayIso && c.lastDone !== todayIso),
    [chores, todayIso],
  );
  const upcoming = useMemo(
    () => chores
      .filter((c) => c.nextDue > todayIso)
      .sort((a, b) => a.nextDue.localeCompare(b.nextDue))
      .slice(0, 5),
    [chores, todayIso],
  );
  const doneToday = useMemo(
    () => chores.filter((c) => c.lastDone === todayIso),
    [chores, todayIso],
  );

  return (
    <div className="chores-today">
      {adults.length === 0 && (
        <div className="chores-empty-card">
          <p>No adult members in this household yet.</p>
          <p className="chores-empty-sub">Add one from Settings → Households and the chart will populate here.</p>
        </div>
      )}

      {adults.length > 0 && (
        <div className="chores-today-grid">
          {adults.map((m) => (
            <PersonColumn
              key={m.userId}
              member={m}
              chores={byPerson.get(m.userId) || []}
              onDone={onDone}
              onEdit={onEdit}
              onDelete={onDelete}
              todayIso={todayIso}
            />
          ))}
          <SideRail
            overdueCount={overdue.length}
            upcoming={upcoming}
            doneToday={doneToday}
            todayIso={todayIso}
          />
        </div>
      )}
    </div>
  );
}

// ── Person column ────────────────────────────────────────────────

interface PersonColumnProps {
  member: HouseholdMember;
  chores: AdultChore[];
  onDone: (chore: AdultChore) => void;
  onEdit: (chore: AdultChore) => void;
  onDelete: (chore: AdultChore) => void;
  todayIso: string;
}

function PersonColumn({ member, chores, onDone, onEdit, onDelete, todayIso }: PersonColumnProps) {
  return (
    <section className="chores-col">
      <header className="chores-col-head">
        <span className="chores-avatar">{member.userId.slice(0, 1).toUpperCase()}</span>
        <div className="chores-col-head-body">
          <div className="chores-col-name">{member.userId}</div>
          <div className="chores-col-meta">
            {chores.length} {chores.length === 1 ? "thing" : "things"}
          </div>
        </div>
      </header>
      <div className="chores-list">
        {chores.length === 0 ? (
          <div className="chores-empty-cell">
            <em>{member.userId} is set.</em>
          </div>
        ) : (
          chores.map((c) => (
            <ChoreRow
              key={c.id}
              chore={c}
              onDone={() => onDone(c)}
              onEdit={() => onEdit(c)}
              onDelete={() => onDelete(c)}
              todayIso={todayIso}
            />
          ))
        )}
      </div>
    </section>
  );
}

// ── Single chore row ─────────────────────────────────────────────

interface ChoreRowProps {
  chore: AdultChore;
  onDone: () => void;
  onEdit: () => void;
  onDelete: () => void;
  todayIso: string;
}

function ChoreRow({ chore, onDone, onEdit, onDelete, todayIso }: ChoreRowProps) {
  const overdue = chore.nextDue < todayIso && chore.lastDone !== todayIso;
  // Completion-pop animation: hold a `popping` flag for ~360ms after
  // the user taps done so the card can scale-pop + particles fly out.
  const [popping, setPopping] = useState(false);
  const handleDone = () => {
    setPopping(true);
    window.setTimeout(() => setPopping(false), 540);
    onDone();
  };
  return (
    <div
      className="chores-row tc-card-anim"
      data-overdue={overdue}
      data-popping={popping ? "true" : undefined}
    >
      <button className="chores-check" type="button" onClick={handleDone} title="Mark done">
        <Icon name="check" size={12} />
      </button>
      <span className="chores-hue" style={{ background: `oklch(0.75 0.16 ${chore.hue})` }} />
      {chore.icon && <span className="tc-icon" aria-hidden>{chore.icon}</span>}
      <div className="chores-row-body">
        <div className="chores-row-name">{chore.name}</div>
        <div className="chores-row-meta">
          {chore.category && (
            <>
              <span>{chore.category}</span>
              <span className="chores-row-dot">·</span>
            </>
          )}
          <span>{chore.frequency}</span>
          {chore.assignmentMode === "rotating" && (
            <>
              <span className="chores-row-dot">·</span>
              <span className="chores-rotates">
                <Icon name="share" size={9} /> rotates
              </span>
            </>
          )}
          {chore.assignmentMode === "parallel" && (
            <>
              <span className="chores-row-dot">·</span>
              <span className="chores-rotates">
                <Icon name="share" size={9} /> parallel
              </span>
            </>
          )}
          {chore.stars > 0 && (
            <>
              <span className="chores-row-dot">·</span>
              <span className="chores-row-stars">⭐ {chore.stars}</span>
            </>
          )}
        </div>
      </div>
      {chore.assignmentMode === "parallel" && chore.assignees.length > 0 && (
        <span className="tc-avatars" aria-hidden>
          {chore.assignees.map((a) => (
            <span
              key={a}
              className="tc-avatar"
              style={{ background: hueToOklch(hashHue(a)) }}
              title={a}
            >{a.charAt(0).toUpperCase()}</span>
          ))}
        </span>
      )}
      {popping && <BurstFx hue={chore.hue} />}
      <ChoreMenu onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}

/** Particle burst — 5 small dots fanning out from the card center,
 *  colored from the chore's hue. Pure CSS animation via custom props. */
function BurstFx({ hue }: { hue: number }) {
  const particles = [
    { dx: -36, dy: -28 },
    { dx:  36, dy: -28 },
    { dx: -22, dy:  30 },
    { dx:  22, dy:  30 },
    { dx:   0, dy: -40 },
  ];
  return (
    <span className="tc-burst" aria-hidden>
      {particles.map((p, i) => (
        <span
          key={i}
          className="tc-burst-particle"
          style={{
            ["--dx" as any]: `${p.dx}px`,
            ["--dy" as any]: `${p.dy}px`,
            ["--burst-color" as any]: `oklch(0.75 0.16 ${hue})`,
          }}
        >⭐</span>
      ))}
    </span>
  );
}

/** Cheap hash → hue 0..360 for an avatar circle. Stable so the same
 *  user id always gets the same color across renders + sessions. */
function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}
function hueToOklch(hue: number): string {
  return `oklch(0.72 0.16 ${hue})`;
}

// ── Per-row "⋯" menu — Edit + Delete ─────────────────────────────
// Inline popover (not a full sheet) so the action stays close to the
// chore being changed. Click-outside closes, ESC closes, ENTER on the
// trigger toggles. Two items only for now; Delete remains amber/dim
// to discourage misclicks.

interface ChoreMenuProps {
  onEdit: () => void;
  onDelete: () => void;
}

function ChoreMenu({ onEdit, onDelete }: ChoreMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      const w = wrapRef.current;
      if (w && !w.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDocPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="chores-row-menu-wrap" ref={wrapRef}>
      <button
        className="chores-row-more"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        title="More"
      >
        <Icon name="more" size={14} />
      </button>
      {open && (
        <div className="chores-row-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="chores-row-menu-item"
            onClick={() => { setOpen(false); onEdit(); }}
          >
            <Icon name="edit" size={12} /> Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="chores-row-menu-item chores-row-menu-danger"
            onClick={() => { setOpen(false); onDelete(); }}
          >
            <Icon name="x" size={12} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ── Side rail ─────────────────────────────────────────────────────

interface SideRailProps {
  overdueCount: number;
  upcoming: AdultChore[];
  doneToday: AdultChore[];
  todayIso: string;
}

function SideRail({ overdueCount, upcoming, doneToday, todayIso }: SideRailProps) {
  return (
    <aside className="chores-side">
      {overdueCount > 0 && (
        <div className="chores-side-block">
          <div className="eyebrow chores-side-amber">
            Behind · whenever you can
          </div>
          <div className="chores-side-sub">
            {overdueCount} thing{overdueCount === 1 ? "" : "s"} carrying over
          </div>
        </div>
      )}

      <div className="chores-side-block">
        <div className="eyebrow">Coming up</div>
        {upcoming.length === 0 ? (
          <div className="chores-side-sub" style={{ marginTop: 6 }}>Nothing scheduled.</div>
        ) : (
          <div className="chores-side-list">
            {upcoming.map((c) => (
              <div key={c.id} className="chores-mini">
                <span className="chores-mini-dot" style={{ background: `oklch(0.75 0.16 ${c.hue})` }} />
                <span className="chores-mini-name">{c.name}</span>
                <span className="chores-mini-due">{relativeDate(c.nextDue, todayIso)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {doneToday.length > 0 && (
        <div className="chores-side-block">
          <div className="eyebrow">Done today</div>
          <div className="chores-side-list">
            {doneToday.map((c) => (
              <div key={c.id} className="chores-mini" data-done>
                <span className="chores-mini-dot" style={{ background: `oklch(0.75 0.16 ${c.hue})` }} />
                <span className="chores-mini-name">{c.name}</span>
                <Icon name="check" size={11} />
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}

// "today" / "tomorrow" / "in 3d" / "May 30" — same rules as the
// design handoff.
function relativeDate(iso: string, todayIso: string): string {
  if (!iso) return "never";
  const target = new Date(`${iso}T00:00:00Z`);
  const today = new Date(`${todayIso}T00:00:00Z`);
  const diff = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return "today";
  if (diff === 1) return "tomorrow";
  if (diff === -1) return "yesterday";
  if (diff > 0 && diff <= 6) return `in ${diff}d`;
  if (diff < 0 && diff >= -6) return `${Math.abs(diff)}d ago`;
  return target.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
